import React, { useState, useContext } from "react";
import { Link, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import * as Sentry from "@sentry/react";
import * as braze from "@braze/web-sdk";
import { usePopup } from "components/common/wrappers/withPopup";
import axios from "axios";
import SignupConfirmationModal from "common/SignupConfirmationModal";
import { isValidEmail, trackLead, useQueryParamsAsJson } from "@tomocredit/common-fe/utils";
import { ANALYTICS_EVENTS, LEAD_SOURCES } from "@tomocredit/common-fe/consts";
import { BreakpointContext } from "components/common";
import Email from "common/EmailV2";
import AutoplayCarousel from "common/AutoplayCarousel";
import TestimonialCard1 from "images/boost/boost_testimonial_card-1.webp";
import TestimonialCard2 from "images/boost/boost_testimonial_card-2.webp";
import TestimonialCard3 from "images/boost/boost_testimonial_card-3.webp";
import TestimonialCard4 from "images/boost/boost_testimonial_card-4.webp";
import Guard from "images/guard.svg";
import BusinessLandingImage from "images/kosmofi/business_landing.webp";
import BoostAi from "images/boost/boost-ai.webp";
import BoostAiMobile from "images/boost/boost-ai-mb.webp";
import BoostCmd from "images/boost/boost-cmd.webp";
import BoostCmdMobile from "images/boost/boost-cmd-mb.webp";
import BoostLoan from "images/boost/boost-loan.webp";
import BoostDispute from "images/boost/boost-dispute.webp";
import { CheckMark } from "@tomocredit/common-fe/components";
import { socialProofImages } from "../../utils";

const GetStartedButton = ({ queryParameters }) => {
  return (<Link
    to={`/boost-pricing${queryParameters.size ? `?${queryParameters.toString()}` : ""}`}
    className="bg-boost-primary-500 px-4 py-2"
    style={{ borderRadius: 4 }}
  >
    <span className="font-bold text-md">Get started</span>
  </Link>
  );
};

export const BoostLandingBusiness = () => {
  const { isBelow } = useContext(BreakpointContext);
  const belowMd = isBelow("md");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modalEmail, setModalEmail] = useState("");
  const [modalState, setModalState] = useState("");
  // const { promoInfo } = useContext(PromoContext);
  const history = useHistory();
  const { hideExitPopup, hidePromoPopupRef } = usePopup();
  const { capturedUrlParams, rawUrlParams } = useQueryParamsAsJson(["email"]);
  const queryParameters = new URLSearchParams(window.location.search);

  const closeModal = () => {
    setModalState("");
    setModalEmail("");
  };

  const handleSubmit = async (email) => {
    if (loading) {
      return;
    }
    setError(null);
    if (!email) {
      setError("Email address is required");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Invalid email address");
      return;
    }
    setLoading(true);

    let signUpBoostRes;
    try {
      const payload = { email, source: LEAD_SOURCES.BOOST_LANDING, capturedUrlParams };
      signUpBoostRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/boost/signup`,
        payload,
      );
    } catch (err) {
      setError("Could not communicate with server. Please try again later");
      return;
    } finally {
      setLoading(false);
    }

    const { userExists, loginExist, boostEnrolled, guid } = signUpBoostRes?.data ?? {};

    if (guid) {
      braze.changeUser(guid);
      braze.getUser()?.setEmail(email);
    }

    if (loginExist && boostEnrolled) {
      hideExitPopup();
      window.location.assign(
        `${
          process.env.REACT_APP_DOMAIN
        }/dashboard/login?redirect_uri=/boost&email=${encodeURIComponent(
          email,
        )}&existingAccount=true&fromBoost=true`,
      );
      return;
    }
    if (!loginExist && boostEnrolled) {
      setModalState("boostEnrolledWithNoLogin");
      return;
    }
    if (!boostEnrolled) {
      const redirectToStripe = () => {
        // redirect to stripe
        rawUrlParams.set("email", email);
        const newUrl = `/boost-pricing?${rawUrlParams.toString()}`;
        history.push(newUrl);
      };

      trackLead({ email });
      if (userExists) {
        redirectToStripe();
      } else {
        window.gtag?.("event", ANALYTICS_EVENTS.NEW_LEAD, {
          email,
          source: LEAD_SOURCES.BOOST_LANDING,
        });
        window?.gtag?.("event", "boost_new_signup", {
          email,
          event_callback: redirectToStripe,
        });
        window.uetq?.push('event', 'lead',
          {
            event_label:"lead",
            event_category:"lead",
            contact: email,
          },
        );
        if (typeof window.gtag !== "function") {
          Sentry.captureException(new Error(`New signup detected in ${LEAD_SOURCES.BOOST_LANDING} but gtag is not loaded`));
        }
      }
      return;
    }

    // user not exist
    setError("Could not communicate with server. Please try again later");
  };

  return (
    <div
      className="flex flex-col md:pt-0 justify-center text-white leading-normal"
      style={{ paddingBottom: "30px", letterSpacing: 0.1 }}
    >
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      {/* <BoostChatBotScript/> */}
      {/* BANNER SECTION */}
      <div
        className="flex w-full justify-evenly items-center xl:flex-row lg:flex-row md:flex-row flex-col-reverse px-6 md:px-10 mx-auto"
        style={{ maxWidth: 1300 }}
      >
        <div className="md:w-1/2">
          <div className="pt-6 hidden md:block">
            <h1 className="mx-2 text-center md:text-left text-5xl xl:text-6xl xl:banner-title banner-title-sm font-semibold pw-7/12 md:w-full tracking-tight leading-none sm:-mt-10 md:mt-0">
              <span>Build Credit Fast</span>
              <span className="text-boost-primary-500">.</span>
            </h1>
            <h1 className="mx-2 text-center md:text-left text-5xl xl:text-4xl xl:banner-title banner-title-sm font-semibold pw-7/12 md:w-full tracking-tight leading-none sm:-mt-10 md:mt-4">
              <span>Unlock Business Capital</span>
              <span className="text-boost-primary-500">.</span>
            </h1>
          </div>
          <div className="flex flex-col-reverse md:flex-col items-center md:items-start text-left self-center mt-4 md:mt-0 w-full whitespace-nowrap">
            <div>
              <div className="py-2 text-lg md:text-xl w-1/2">
                <CheckMark className="my-4" color="boost">
                  <span className="ml-3">
                    Up to <span className="font-bold">$30,000</span> credit line
                  </span>
                </CheckMark>
                <CheckMark className="my-4" color="boost">
                  <span className="ml-3">
                    <span className="font-bold">105+ </span>
                    <span>credit Boost</span>
                  </span>
                </CheckMark>
                <CheckMark className="my-4" color="boost">
                  <span className="ml-3 relative">
                    No credit checks. <span className="font-bold">Ever.</span>
                  </span>
                </CheckMark>
              </div>
              <div className="self-center md:self-start">
                <div
                  className="mt-2 flex flex-col lg:flex-row gap-x-4 gap-y-10 box-border"
                  style={{ maxWidth: 507 }}
                >
                  <Email
                    buttonText="Boost my score"
                    handleSubmit={handleSubmit}
                    componentClassName="flex-col md:flex-row space-y-2 md:space-y-0"
                    inputClassName="w-full rounded-md md:rounded-r-none border border-neutral-500 md:border-r-0 placeholder-gray-600 text-black bg-white"
                    buttonClassName="bg-boost-button-purple text-white w-full md:w-auto rounded-md md:rounded-l-none border border-boost-primary-500 font-semibold"
                    onFocus={() => { hidePromoPopupRef.current = true; }}
                    onBlur={() => { hidePromoPopupRef.current = false; }}
                  />
                </div>
                {error && (
                  <p className="text-sm bg-black text-error-200 text-left ml-2 mt-2">
                    {error}
                  </p>
                )}
                <div className="mt-8 text-base flex items-center text-left text-boost-price-green whitespace-pre-wrap w-full">
                  <img src={Guard} alt="" className="inline mr-2 h-4" />
                  <span>
                    Trusted by <span className="font-bold">4 million+</span>{" "}
                    people building credit with TomoBoost
                  </span>
                </div>
              </div>
              {modalState && (
                <SignupConfirmationModal
                  email={modalEmail}
                  closeModal={closeModal}
                  modalState={modalState}
                />
              )}
            </div>
          </div>
        </div>
        <div className="mx-auto text-center md:block px-10 relative">
          <div
            className="md:hidden absolute z-50 self-center"
            style={{
              top: "50%",
              left: "50%",
              transform: "translateY(-50%) translateX(-50%)",
            }}
          >
            <h1
              className="mx-2 text-center md:text-left font-bold whitespace-nowrap mb-4"
              style={{ fontSize: 40 }}
            >
              <span>Build Credit Fast</span>
              <span className="text-boost-primary-500">.</span>
            </h1>
            <h1
              className="mx-2 text-center md:text-left font-bold"
              style={{ fontSize: 32 }}
            >
              <span>Unlock Business Capital</span>
              <span className="text-boost-primary-500">.</span>
            </h1>
          </div>
          <div className="opacity-50 md:opacity-100 z-0 mx-6 md:mx-0">
            <img src={BusinessLandingImage} alt="" className="md:w-[423px]" />
          </div>
        </div>
      </div>
      {/* TESTIMONIAL CARDS SECTION */}
      <div
        className="mt-10 md:mt-24 flex flex-col justify-center items-center mx-auto"
        style={{ maxWidth: 1300 }}
      >
        <div className="font-bold text-2xl text-neutral-500">Hear what others have to say</div>
        <div className="flex overflow-x-scroll hide-x-scrollbar shrink-0 w-full">
          <div className="mx-6 md:mx-10 mt-5 pb-10 flex self-start overflow-visible">
            {
              [TestimonialCard1, TestimonialCard2, TestimonialCard3, TestimonialCard4]
                .map((imageSrc, idx) => (
                  <img
                    alt=""
                    src={imageSrc}
                    // eslint-disable-next-line react/no-array-index-key
                    key={`testimonial-${idx}`}
                    style={{ width: 295, height: "auto" }}
                    className="pr-6 box-content transform hover:scale-110 transition-transform"
                  />
                ))
            }
          </div>
        </div>
      </div>
      {/* SOCIAL PROOF CAROUSEL */}
      <div className="py-8 bg-neutral-300">
        <AutoplayCarousel
          titleText="As mentioned in"
          duplicationFactor={3}
          totalWidthPreload={7752} // will need to change this if adding/removing image
          items={socialProofImages.map((item) => {
            return (
              <div className="" style={{ height: 30 }}>
                <img src={item.img} alt={item.alt} className="" />
              </div>
            );
          })}
        />
      </div>
      {/* FEATURES */}
      <div
        className="px-6 md:px-10 mx-auto"
        style={{ maxWidth: 1300 }}
      >
        <h2 className="font-bold text-2xl mt-8 mb-16 md:my-10 text-center" style={{ letterSpacing: -0.25 }}>We have the right tools for you</h2>
        {/* BOOST LOAN */}
        <div
          className="my-24 md:my-0 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 box-border"
          style={{ height: belowMd ? undefined : 502 }}
        >
          <div className="flex justify-center items-center order-1 md:order-2 md:pl-4">
            <img alt="" src={BoostLoan} className="" style={{ width: 303, height: "auto", objectFit: "cover" }} />
          </div>
          <div className="flex justify-center items-center order-2 md:order-1">
            <div className="bg-neutral-700 px-6 py-8 md:px-10 md:pt-[60px] md:pb-[45px] flex-1 flex flex-col rounded-lg shrink-0 text-left">
              <div className="font-bold leading-tight" style={{ fontSize: belowMd ? 28 : 36 }}>
                Credit building loan<span className="text-boost-primary-500">.</span>
              </div>
              <div className="mt-6"><span className="font-bold">Coming soon:</span> Raise your credit score and expand your credit file can lead to more card and loan offers.</div>
              <div className="mt-6 h-10 flex">
                <GetStartedButton queryParameters={queryParameters}/>
              </div>
            </div>
          </div>
        </div>
        {/* BOOST DISPUTE */}
        <div
          className="my-24 md:my-0 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 box-border"
          style={{ height: belowMd ? undefined : 502 }}
        >
          <div className="flex justify-center items-center order-1 md:pr-4">
            <img alt="" src={BoostDispute} className="" style={{ width: 314, height: "auto", objectFit: "cover" }} />
          </div>
          <div className="flex justify-center items-center order-2">
            <div className="bg-neutral-700 px-6 py-8 md:px-10 md:pt-[60px] md:pb-[45px] flex-1 flex flex-col rounded-lg shrink-0 text-left">
              <div className="font-bold leading-tight" style={{ fontSize: belowMd ? 28 : 36 }}>
                Fix damaging report mistakes<span className="text-boost-primary-500">.</span>
              </div>
              <div className="mt-6">Easily file disputes on credit report inaccuracies to maintain a healthy score.</div>
              <div className="mt-6 h-10 flex">
                <GetStartedButton queryParameters={queryParameters}/>
              </div>
            </div>
          </div>
        </div>
        {/* BOOST CMD */}
        <div
          className="my-24 md:my-0 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 box-border"
          style={{ height: belowMd ? undefined : 502 }}
        >
          <div className="mb-8 flex justify-center items-center order-1 md:order-2 md:pl-4">
            <img alt="" src={belowMd ? BoostCmdMobile : BoostCmd} className="" style={{ width: belowMd ? 218 : 493, height: "auto", objectFit: "cover" }} />
          </div>
          <div className="flex justify-center items-center order-2 md:order-1">
            <div className="bg-neutral-700 px-6 py-8 md:px-10 md:pt-[60px] md:pb-[45px] flex-1 flex flex-col rounded-lg shrink-0 text-left">
              <div className="font-bold leading-tight" style={{ fontSize: belowMd ? 28 : 36 }}>
                Realtime credit score updates<span className="text-boost-primary-500">.</span>
              </div>
              <div className="mt-6">
                <div>Monitor and safeguard your credit score and track your subscriptions all in one place.</div>
              </div>
              <div className="mt-6 h-10 flex">
                <GetStartedButton queryParameters={queryParameters}/>
              </div>
            </div>
          </div>
        </div>
        {/* BOOST AI */}
        <div
          className="my-24 md:my-0 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 box-border"
          style={{ height: belowMd ? undefined : 502 }}
        >
          <div className="flex justify-center items-center order-1 md:pr-4">
            <img alt="" src={belowMd ? BoostAiMobile : BoostAi} className="" style={{ width: belowMd ? 304 : 433, height: "auto", objectFit: "cover" }} />
          </div>
          <div className="flex justify-center items-center order-2">
            <div className="bg-neutral-700 px-6 py-8 md:px-10 md:pt-[60px] md:pb-[45px] flex-1 flex flex-col rounded-lg shrink-0 text-left">
              <div className="font-bold leading-tight" style={{ fontSize: belowMd ? 28 : 36 }}>
                AI financial advisor<span className="text-boost-primary-500">.</span>
              </div>
              <div className="mt-6">Instant personalized answers to make smarter and confident financial decisions.</div>
              <div className="mt-6 h-10 flex">
                <GetStartedButton queryParameters={queryParameters}/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
